import { useMemo, useState } from 'react';
import { obterQrDoLote } from '../../api/lotes.js';
import type { Lote } from '../../api/lotes.js';
import { Botao } from '../../componentes/Botao.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { IconeEtiqueta } from '../../componentes/Icones.js';
import { DesenhoDaEtiquetaDeLote } from '../etiquetas/DesenhoDaEtiquetaDeLote.js';
import { TAMANHO_PADRAO, tamanhoDeEtiqueta } from '../etiquetas/etiqueta.js';
import { validarCodLot } from '../etiquetas/etiqueta-lote.js';
import { FolhaDeEtiquetas } from '../etiquetas/FolhaDeImpressao.js';
import { LOTE_NAO_CABE_NO_QR } from '../etiquetas/PreviaDasEtiquetasDeLote.js';
import { useQrPorCodigo } from '../etiquetas/useQrPorCodigo.js';
import './QrDoProduto.css';

/** 404 sem código: o middleware publicado ainda não tem `GET /lotes/qrcode`. */
const ROTA_AUSENTE = 'NAO_ENCONTRADO';

const MENSAGEM_DE_ROTA_AUSENTE =
  'O servidor ainda não gera o QR de lote. A etiqueta fica disponível quando o middleware for atualizado.';

const SEM_PEDIDO: readonly string[] = [];

function PreviaDoQrDoLote({
  lote,
  aoFechar,
}: {
  readonly lote: Lote;
  readonly aoFechar: () => void;
}) {
  // Lote que não cabe no QR nem vai ao middleware: a resposta seria o mesmo 400.
  const recusa = validarCodLot(lote.codLot);
  const codigos = useMemo(
    () => (recusa === null ? [lote.codLot] : SEM_PEDIDO),
    [recusa, lote.codLot],
  );
  const { estados, tentarDeNovo } = useQrPorCodigo(codigos, obterQrDoLote);
  const estado = estados.get(lote.codLot);
  const tamanho = tamanhoDeEtiqueta(TAMANHO_PADRAO);
  const qr = estado?.situacao === 'pronto' ? estado.qr : null;
  const falha =
    recusa ??
    (estado?.situacao === 'falhou'
      ? estado.codigo === ROTA_AUSENTE
        ? MENSAGEM_DE_ROTA_AUSENTE
        : estado.mensagem
      : null);
  const podeTentarDeNovo =
    estado?.situacao === 'falhou' &&
    estado.codigo !== ROTA_AUSENTE &&
    estado.codigo !== LOTE_NAO_CABE_NO_QR;

  return (
    <DialogoConfirmacao
      titulo={`QR Code do lote ${lote.codLot}`}
      rotuloConfirmar="Imprimir"
      rotuloCancelar="Fechar"
      variante="primario"
      confirmacaoBloqueada={qr === null}
      aoConfirmar={() => {
        window.print();
      }}
      aoCancelar={aoFechar}
    >
      <div className="qr-do-produto">
        {qr !== null && <DesenhoDaEtiquetaDeLote tamanho={tamanho} qr={qr} lote={lote} />}

        {falha !== null && (
          <div className="qr-do-produto__falha" role="alert">
            <p>{falha}</p>
            {podeTentarDeNovo && (
              <Botao
                altura="pequena"
                onClick={() => {
                  tentarDeNovo(lote.codLot);
                }}
              >
                Tentar de novo
              </Botao>
            )}
          </div>
        )}

        {falha === null && qr === null && (
          <p className="qr-do-produto__nota" role="status">
            Gerando o QR do lote {lote.codLot}…
          </p>
        )}

        <p className="qr-do-produto__nota">
          Etiqueta de {tamanho.rotulo}, uma por página. Tamanho e cópias se escolhem em Etiquetas ›
          Por lote.
        </p>
      </div>

      {qr !== null && (
        <FolhaDeEtiquetas tamanho={tamanho}>
          <div className="folha-etiquetas__pagina">
            <DesenhoDaEtiquetaDeLote tamanho={tamanho} qr={qr} lote={lote} emMilimetros />
          </div>
        </FolhaDeEtiquetas>
      )}
    </DialogoConfirmacao>
  );
}

/**
 * Ação "QR Code" de uma linha de lote de produto: prévia da etiqueta de lote
 * com a matriz de `GET /lotes/qrcode` e impressão só dela, pela mesma folha
 * da tela de Etiquetas. Quem renderiza já conferiu `etiquetas.imprimir`.
 */
export function QrDoLote({ lote }: { readonly lote: Lote }) {
  const [aberto, definirAberto] = useState(false);

  return (
    <>
      <Botao
        variante="discreto"
        altura="pequena"
        aria-label={`QR Code do lote ${lote.codLot}`}
        onClick={() => {
          definirAberto(true);
        }}
      >
        <IconeEtiqueta tamanho={14} />
        QR Code
      </Botao>
      {aberto && (
        <PreviaDoQrDoLote
          lote={lote}
          aoFechar={() => {
            definirAberto(false);
          }}
        />
      )}
    </>
  );
}
